import { Button, Icon, Stack, Flex, SearchInput, Box } from '@tonic-one/react';
import { AddIcon, ExportIcon, ImportIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NavBack from 'src/components/Common/NavBack';
import BottomPagination from '../../../Report/BottomPagination';
import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import ComplianceTemplatesTable from './Table';
import { DlpTemplate, DpBuiltinEnum, FilterParams } from '../../utils/types';
import { PolicyApi } from '@Apis';
import { TemplateModal } from './Modal';
import { DlpTemplateModalNameEnum, DlpTemplateModalState } from './type';
import { usePermissionStore } from '../../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { exportFile } from '../../../../utils/common';
import CSpinner from '@Components/CSpinner';
import i18next from 'i18next';
import { FileType } from '../../../../types/globalSetting';
import ImportModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ImportModal';
import { useLayoutStore } from '@Models/useLayoutModel';

const ComplianceTemplates = () => {
  const { t } = useTranslation(['common', 'policy']);
  const navigate = useNavigate();
  const { layoutHeight } = useLayoutStore();
  const [filterParams, setFilterParams] = useState<FilterParams>({
    keyword: '',
    offset: 1,
    size: 50,
  });
  const [data, setData] = useState<DlpTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<DlpTemplate[]>([]);
  const [modalState, setModalState] = useState<DlpTemplateModalState>({
    name: DlpTemplateModalNameEnum.None,
    data: null,
  });
  const onImport = async (content: FormData) => {
    try {
      setLoading(true);
      await PolicyApi.importDlpEntity(content);
      setIsImportModalVisible(false);
      setRefreshTableFlag(new Date());
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isExportVisible, setIsExportVisible] = useState(false);
  const { widthEditablePermission } = usePermissionStore();
  const dlpEditPermission = widthEditablePermission(
    PermissionNameEnum.dlpPolicy
  );
  console.log('filterParams', filterParams);
  const setFilterParamsWithDebounce = debounce(setFilterParams, 200);

  const [refreshTableFlag, setRefreshTableFlag] = useState(new Date());
  const refreshTable = useCallback(() => {
    setRefreshTableFlag(new Date());
  }, []);
  const onCloseModal = useCallback(() => {
    setModalState({
      name: DlpTemplateModalNameEnum.None,
      data: null,
    });
  }, []);
  const onClickDownload = async () => {
    for (const row of selectedRows) {
      if (row.dpTemplateBuiltin === DpBuiltinEnum.Predefined) {
        window.showNotification({
          titleMsg: t('policy.complianceTemplates.exportWarning'),
          severity: 'warning',
        });
        return;
      }
    }

    try {
      setLoading(true);
      const res = await PolicyApi.downloadDlpTemplate(
        selectedRows.map((v) => v.cfgIndex)
      );
      exportFile(
        'templates_export.xml',
        res.data as any,
        'application/octet-stream'
      );
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  // const cellHeight = '36px';
  useEffect(() => {
    PolicyApi.getDlpTemplates(i18next.language).then((res) => {
      setData(res.data);
    });
  }, [refreshTableFlag]);
  useEffect(() => {
    if (selectedRows.length !== 0) {
      setIsExportVisible(true);
    } else {
      setIsExportVisible(false);
    }
  }, [selectedRows]);

  return (
    <Box>
      {loading && <CSpinner />}
      <ImportModal
        isVisible={isImportModalVisible}
        onClose={() => {
          setIsImportModalVisible(false);
        }}
        onImport={onImport}
        acceptFileType={FileType.XML}
      />
      <Stack position="relative" height={layoutHeight}>
        <NavBack
          fixed={false}
          onBack={() => {
            navigate(-1);
          }}
          style={{ padding: '7px 24px 7px 7px' }}
        />
        <Flex
          justifyContent="space-between"
          height="52px"
          p="10px 16px 10px 16px"
        >
          <Flex height="32px" gap="8px">
            <Button
              variant="primary"
              onClick={() => {
                setModalState({
                  name: DlpTemplateModalNameEnum.Add,
                  data: null,
                });
                console.log('add');
              }}
              disabled={!dlpEditPermission}
            >
              <Icon as={AddIcon} mr="2x" size="16px" />
              {t('common.action.label.add')}
            </Button>
            <SearchInput
              width="244px"
              placeholder={t('common.label.nameAndDesc')}
              onChange={(e: any) => {
                setFilterParamsWithDebounce({
                  keyword: e.target.value,
                  offset: 1,
                  size: 50,
                });
              }}
              onClearInput={() => {
                setFilterParamsWithDebounce({
                  keyword: '',
                  offset: 1,
                  size: 50,
                });
              }}
            />
          </Flex>
          <Flex height="32px" gap="8px">
            <Button
              variant="secondary"
              disabled={!dlpEditPermission}
              onClick={() => {
                setIsImportModalVisible(true);
              }}
            >
              <Icon as={ImportIcon} mr="2x" size="16px" />
              {t('common.action.label.import')}
            </Button>
            <Button
              variant="secondary"
              onClick={onClickDownload}
              disabled={!isExportVisible}
            >
              <Icon as={ExportIcon} mr="2x" size="16px" />
              {t('common.action.label.export')}
            </Button>
          </Flex>
        </Flex>
        <ComplianceTemplatesTable
          key={refreshTableFlag.toUTCString()}
          data={data}
          setSelectedRows={setSelectedRows}
          filterParams={filterParams}
          refreshTable={refreshTable}
          setModalState={setModalState}
        />
        {/* 其他三个组件的高度分别是 46 + 52 + 48 */}
        <BottomPagination
          totalCount={data.length}
          currentPage={filterParams.offset}
          pageSize={filterParams.size}
          changePageSize={(size: number) => {
            setFilterParamsWithDebounce({
              ...filterParams,
              size,
              offset: 1,
            });
          }}
          changeCurrentPage={(offset: number) => {
            setFilterParamsWithDebounce({
              ...filterParams,
              offset,
            });
          }}
        />
        {modalState.name !== DlpTemplateModalNameEnum.None && (
          <TemplateModal
            modalState={modalState}
            onClose={onCloseModal}
            refreshTable={refreshTable}
          />
        )}
      </Stack>
    </Box>
  );
};

export default ComplianceTemplates;
